import {
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  AgentListProjectsParams,
  AgentToggleProjectLinkActiveInput,
  AgentCreateProjectLinkInput,
  AgentListMyProjectsParams,
  AgentListAvailableProjectsParams,
} from '../api'
import { getAgentProjectDetailsOptions } from './agent-project.options'
import projectService from './agent-project.service'
import { EAgentProjectQueryKey } from './agent-project.types'
import type { IMutationArgs } from '../types'

export const useAgentListProjects = (
  params: AgentListProjectsParams,
  enabled: boolean,
) => {
  return useInfiniteQuery({
    queryKey: [EAgentProjectQueryKey.AGENT_PROJECT, params],
    queryFn: ({ pageParam = 0 }) => {
      return projectService.getAgentListProjects({
        ...params,
        skip: pageParam * (params.limit || 10),
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.data?.total || 0
      const currentLength = allPages.reduce(
        (acc, page) => acc + (page.data?.projects?.length || 0),
        0,
      )
      return currentLength < total ? allPages.length : undefined
    },
    initialPageParam: 0,
    enabled: enabled,
  })
}
export const useAgentListMyProjects = (params: AgentListMyProjectsParams) => {
  return useInfiniteQuery({
    queryKey: [EAgentProjectQueryKey.AGENT_PROJECT, params],
    queryFn: ({ pageParam = 0 }) => {
      return projectService.agentListMyProjects({
        ...params,
        skip: pageParam * (params.limit || 10),
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.data?.total || 0
      const currentLength = allPages.reduce(
        (acc, page) => acc + (page.data?.projects?.length || 0),
        0,
      )
      return currentLength < total ? allPages.length : undefined
    },
    initialPageParam: 0,
  })
}

export const useAgentListAvailableProjects = (
  params: AgentListAvailableProjectsParams,
) => {
  return useInfiniteQuery({
    queryKey: [EAgentProjectQueryKey.AGENT_LIST_AVAILABLE_PROJECTS, params],
    queryFn: ({ pageParam = 0 }) => {
      return projectService.agentListAvailableProjects({
        ...params,
        skip: pageParam * (params.limit || 10),
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.data?.total || 0
      const currentLength = allPages.reduce(
        (acc, page) => acc + (page.data?.projects?.length || 0),
        0,
      )
      return currentLength < total ? allPages.length : undefined
    },
    initialPageParam: 0,
  })
}

export const useGetAgentProjectDetails = (token?: string) => {
  return useQuery(getAgentProjectDetailsOptions(token))
}

export const useAgentCreateProjectLink = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: { data: AgentCreateProjectLinkInput; token: string }) =>
      projectService.agentCreateProjectLink(data.data, data.token),
    mutationKey: [EAgentProjectQueryKey.AGENT_PROJECT],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAgentProjectQueryKey.AGENT_PROJECT],
        })
        queryClient.invalidateQueries({
          queryKey: [EAgentProjectQueryKey.AGENT_LIST_AVAILABLE_PROJECTS],
        })
      }
      return data
    },
  })
}

export const useResetProjects = () => {
  const queryClient = useQueryClient()

  const resetAgentProjects = () => {
    queryClient.invalidateQueries({
      queryKey: [EAgentProjectQueryKey.AGENT_PROJECT],
    })
  }

  const resetAgentListAvailableProjects = () => {
    queryClient.invalidateQueries({
      queryKey: [EAgentProjectQueryKey.AGENT_LIST_AVAILABLE_PROJECTS],
    })
  }

  return {
    resetAgentProjects,
    resetAgentListAvailableProjects,
  }
}
export const useAgentToggleProjectLinkActive = () => {
  return useMutation({
    mutationFn: (data: {
      data: AgentToggleProjectLinkActiveInput
      token: string
    }) => projectService.agentToggleProjectLinkActive(data.data, data.token),
  })
}
